import { getDbClassName } from '@shared/db-class';
import type { Item } from '@shared/types-v0.1.0';
import type { Props } from '../../node/definition';
import type { SchemeData } from '../../node/store';

export type Nodes = Record<string, Node>;
export type NodeSingleSelection = { value: string | null }; // Выбранный ребенок.
export type NodeMultiSelection = { value: MultiSelection };
export type MultiSelection = 'notSelected' | 'selected' | 'indeterminate';
export type NodeExpansion = { value: boolean };
export type Sort = { value: { direction: 'asc' | 'desc'; id?: number } };
export type Filters = { value: Record<string, { enabled: boolean; value?: any; defaultValue?: any }> };
// type Aggregations = Record<string, Record<string, any>>;

// Отличия от useData
// Нет аггрегаций в rxDB

export default class Node {
	rootId: string; // id root node
	itemId?: string; // id proxy item - нет у root node
	dbClass: string; // Класс для данной ноды
	path: string; // Путь в иерархии itemId.parentItemId...rootId
	level: number; // Уровень в иерархии - root = -1
	parentId?: string; // id родительского proxy item - нет у root
	parentPath?: string; // Путь в иерархии parentItemId...rootId
	childIds: string[]; // id дочерних proxy item
	childPathes: string[]; // пути дочерних proxy item
	// aggregations: Aggregations;
	states: {
		// Описание смотри в типах выше
		singleSelection: NodeSingleSelection;
		multiSelection: NodeMultiSelection;
		expansion: NodeExpansion;
		sort: Sort;
		filters: Filters;
	};

	// Создаем класс
	constructor(
		rootId: string,
		dbClass: string,
		path: string,
		level: number,
		childIds: string[],
		childPathes: string[],
		// aggregations: Aggregations,
		itemId?: string,
		parentId?: string,
		parentPath?: string
	) {
		this.rootId = rootId;
		this.dbClass = dbClass;
		this.path = path;
		this.level = level;
		this.childIds = childIds;
		this.childPathes = childPathes;
		this.itemId = itemId;
		this.parentId = parentId;
		this.parentPath = parentPath;
		this.states = {
			singleSelection: { value: null },
			multiSelection: { value: 'notSelected' },
			expansion: { value: false },
			sort: { value: { direction: 'asc' } },
			filters: { value: {} },
		};
		// this.aggregations = aggregations;
	}

	//// Внутренние методы.
	// static методы выхываются у самого класса, а не у его экземпляров. У них нет доступа к this, так как нет самого экземпляра.
	static createHierarchy(p: Props, flatNodes: Node[]) {
		const rootId = p.store.rootId;
		const rootSchemesData = p.store.schemesData.filter((i) => i.path === 'root');

		const rootChildIds = rootSchemesData.flatMap((i) => i.itemIds);

		// Создаем root ноду
		flatNodes.push(
			new Node(
				rootId,
				'rootNode',
				rootId,
				-1,
				rootChildIds,
				rootChildIds.map((id) => `${id}.${rootId}`)
				// aggregations
			)
		);

		// После создания иерархии создадим детей
		Node.createChildren(p, rootSchemesData, rootId, 0, flatNodes);
	}

	static createChildren(p: Props, schemesData: SchemeData[], parentPath: string, level: number, flatNodes: Node[]) {
		for (const thisNodeSchemeData of schemesData) {
			const thisNodeIds = thisNodeSchemeData.itemIds;

			for (const thisNodeItemId of thisNodeIds) {
				let thisNodeChildIds: string[] = [];
				for (const childSchemeData of p.store.schemesData) {
					if (childSchemeData.path === `${thisNodeItemId}.${thisNodeSchemeData.path}`) {
						thisNodeChildIds = thisNodeChildIds.concat(childSchemeData.itemIds);
					}
				}

				flatNodes.push(
					new Node(
						p.store.rootId,
						getDbClassName(thisNodeSchemeData.scheme.dbClass),
						`${thisNodeItemId}.${parentPath}`,
						level,
						thisNodeChildIds,
						thisNodeChildIds.map((childId) => `${childId}.${thisNodeItemId}.${parentPath}`),
						// aggregations,
						thisNodeItemId,
						thisNodeSchemeData.parentId,
						parentPath
					)
				);

				const childSchemesData = p.store.schemesData.filter((i) => i.path === `${thisNodeItemId}.${thisNodeSchemeData.path}`);
				Node.createChildren(p, childSchemesData, `${thisNodeItemId}.${parentPath}`, level + 1, flatNodes);
			}
		}
	}

	// Создаем прокси объект ноды в R.nodes
	static setNodesProxy(p: Props, flatNodes: Node[]) {
		const { map, set } = R.libs.just;
		// Важно не заменять весь прокси.
		// Обновляем/создаем прокси нод
		for (const node of flatNodes) {
			const nodeProxy = R.nodes[node.path] as Node;
			if (nodeProxy) map(node as any, (k, v) => k !== 'states' && set(nodeProxy, k, v));
			else (R.nodes[node.path] as Node) = node;
		}

		// Отфильтровываем ноды, которые принадлежат этому корню, но их нет во flatNode
		for (const node of Object.values(R.nodes).filter(
			(node) => node.rootId === p.store.rootId && !flatNodes.map((i) => i.path).includes(node.path)
		)) {
			// Удаляем такие ноды
			delete R.nodes[node.path];
		}
	}

	//// Методы для разработчика.
	// Возвращает корневую node, для самой данной ноды
	rootNode() {
		return R.nodes[this.rootId];
	}

	// Возвращает родительскую node, для самой данной ноды
	parentNode() {
		return this.parentPath ? (R.nodes[this.parentPath] as Node) : undefined;
	}

	// Возвращает дочерние nodы, для самой данной ноды
	childNodes() {
		return this.childPathes.map((path) => R.nodes[path] as Node).filter((i) => !!i);
	}

	// Возвращает nodы всех потомков, для саой данной ноды
	descendantNodes(withSelf?: boolean) {
		let descendants: Node[] = withSelf ? [this] : [];
		let children = this.childNodes();

		while (children.length) {
			descendants = descendants.concat(children);
			children = children.flatMap((i) => i.childNodes());
		}
		return descendants;
	}

	// Вовзращает nodы всех родителей, для самой данной ноды
	ancestorNodes(withSelf?: boolean) {
		const ancestors: Node[] = withSelf ? [this] : [];
		let parent = this.parentNode();
		while (parent) {
			ancestors.push(parent);
			parent = parent.parentNode();
		}
		return ancestors;
	}

	// Поиск данной ноды в потомках рутовой ноды по её id
	findNode(itemId?: string) {
		return itemId
			? this.rootNode()
					.descendantNodes()
					.find((i) => i.itemId === itemId)
			: undefined;
	}

	// Методы item.
	// Возвращает proxy item для данной ноды
	item() {
		return this.itemId ? R.items[this.itemId] : undefined;
	}

	// Возвращает родительский proxy item для данной ноды
	parent() {
		return this.parentNode()?.item();
	}

	// Возвращает дочерние proxy items указанного класса
	children(dbClass?: string) {
		return filterDbClass(
			this.childNodes().map((i) => i.item()),
			dbClass
		);
	}

	// Возвращает потомков proxy items указанного класса
	descendants(dbClass?: string, withSelf?: boolean) {
		return filterDbClass(
			this.descendantNodes(withSelf).map((i) => i.item()),
			dbClass
		);
	}

	// Возвращает предков proxy items указанного класса
	ancestors(dbClass?: string, withSelf?: boolean) {
		return filterDbClass(
			this.ancestorNodes(withSelf).map((i) => i.item()),
			dbClass
		);
	}

	// Возвращает выбранный proxy item
	singleSelected() {
		const selectedId = this.descendantNodes(true).find((i) => i.states.singleSelection.value)?.states.singleSelection.value;
		return selectedId ? R.items[selectedId] : undefined;
	}

	// Возвращает выбранные дочерние proxy item для указанного класса
	multiSelected(dbClass?: string, withIndeterminate?: boolean) {
		const states = withIndeterminate ? ['selected', 'indeterminate'] : ['selected'];
		const allSelectedNodes = this.descendantNodes().filter((i) => states.includes(i.states.multiSelection.value));
		const selectedItems = filterDbClass(
			allSelectedNodes.map((i) => i.item()),
			dbClass
		);

		return R.libs.remeda.uniqueWith(selectedItems, (a, b) => a.id === b.id);
	}

	// Возвращает развернутые дочерние proxy item для указанного класса
	expanded() {
		return this.descendantNodes()
			.filter((i) => i.states.expansion.value)
			.map((i) => i.item());
	}
}

// Отфильтровывает items по указанному классу
const filterDbClass = (items: (Item | undefined)[], dbClass?: string) =>
	items.filter((i) => (dbClass ? i?.dbClass === dbClass : true)).filter((i) => !!i);
